import React, { useEffect, useState } from 'react';
import { ReactFlow, useNodesState, useEdgesState, Controls, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import documentService from '../../service/newDoc.service';
import { DocumentTypeRoutes } from '../../config/DocumentType';
import usePages from '../../context/PageContext';
import { formatCurrency } from '../../utils/currencyHelper';
import DocDialog from './DocDialog';
import { getDocStatusLabel, StatusColors } from '../../config/document';
import colorFinder from '../../utils/colorFinder';

const snapGrid = [20, 20];

const connectionLineStyle = {
  stroke: '#b1b1b7',
};

const defaultEdgeOptions = {
  type: 'floating',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 17,
    height: 17,
    color: 'rgb(53, 53, 53)',
  },
};

const DocumentContactMap = () => {
  const navigate = useNavigate();
  const { docTypeId, docId } = useParams();
  const location = useLocation();
  const { setPages } = usePages();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [docData, setDocData] = useState([]);
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);

  useEffect(() => {
    if (docTypeId && docId) {
      getAllContactMap();
    }
  }, [docTypeId, docId]);

  const getAllContactMap = () => {
    documentService.getContactMap(docTypeId, docId).then(({ edges, nodes }) => {
      const styledNodes = nodes.map((node) => {
        const isMatching =
          parseInt(docId) === parseInt(node.data.docId) && parseInt(docTypeId) === parseInt(node.data.docTypeId);

        const statusColor = StatusColors[node.data.docStatus];
        const color = colorFinder(statusColor);

        return {
          ...node,
          data: {
            ...node.data,
            label: (
              <Box>
                <Box
                  backgroundColor={isMatching ? '#fff0b3' : '#E9F5FE'}
                  padding="0.3rem 0rem"
                  color={isMatching ? '#F9BC04' : '#0076f8'}
                >
                  <Typography fontWeight="bold">{node.data.label}</Typography>
                </Box>
                <Box padding="0.5rem 0.5rem 0.5rem 0rem">
                  <Box textAlign="right" marginLeft="auto" width="fit-content">
                    <Typography
                      fontSize="small"
                      p="0rem 0.2rem"
                      sx={{ color: color?.textColor, background: color?.colorBgCode }}
                    >
                      {getDocStatusLabel(node.data.docStatus, Boolean(node.data.invoice))}
                    </Typography>
                  </Box>
                  <Typography fontSize="small" textAlign="right">
                    {node.data.docNum}
                  </Typography>
                  <Typography fontSize="small" textAlign="right">
                    {dayjs(node.data.docDate).format('YYYY-MM-DD')}
                  </Typography>
                  <Typography fontSize="small" textAlign="right">
                    {`Nettó: ${formatCurrency(node.data.net)} ${node.data.currencyCode}`}
                  </Typography>
                  <Typography fontSize="small" textAlign="right">
                    {`Áfa: ${formatCurrency(node.data.vat)} ${node.data.currencyCode}`}
                  </Typography>
                  <Typography fontSize="small" textAlign="right">
                    {`Bruttó: ${formatCurrency(node.data.gross)} ${node.data.currencyCode}`}
                  </Typography>
                </Box>
              </Box>
            ),
          },
          style: {
            border: 'none',
            color: 'grey',
            minWidth: '200px',
            padding: 0,
          },
        };
      });

      setNodes(styledNodes);
      setEdges(edges);
    });
  };

  const handleBack = async () => {
    const doc = await documentService.getDocumentById(docId, docTypeId);
    if (doc && docTypeId && docId) {
      navigate(`/app/${DocumentTypeRoutes[docTypeId]}`, {
        state: {
          contactMap: doc,
        },
      });
      setPages({ subMenuName: location.state.docName });
    }
  };

  const onNodeClick = (_event, node) => {
    documentService.getDocumentById(node.data.docId, node.data.docTypeId).then((data) => {
      setIsDocDialogOpen(true);
      setDocData({ ...data, docName: node.data.name });
    });
  };

  return (
    <>
      {docTypeId && docId && (
        <div className="fixed z-40 m-2">
          <IconButton size="small" onClick={handleBack}>
            <div className="text-sm font-medium py-1.5 px-2 rounded flex items-center justify-center gap-1 bg-ganttHoverButtonColor text-white">
              <KeyboardBackspaceIcon className="text-xl" />
              <span className="text-base">Vissza</span>
            </div>
          </IconButton>
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        snapToGrid={true}
        onNodeClick={onNodeClick}
        style={{ background: '#CDDFF7' }}
        snapGrid={snapGrid}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        connectionLineStyle={connectionLineStyle}
        attributionPosition="bottom-left"
      >
        <Controls />
      </ReactFlow>

      <DocDialog
        open={isDocDialogOpen}
        handleClose={() => setIsDocDialogOpen(false)}
        data={docData}
        docTypeId={docTypeId}
      />
    </>
  );
};

export default DocumentContactMap;
